import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { orderBy } from 'lodash'

import { AnalyticsCommitment } from './utils/types'
import { AnalyticsField, DuckDBSchemaType, FilterField } from '@types'

import { ANALYTICS_CAPTIONS } from '@/const'
import { COMMITMENTS_POSIBLE_VALUES } from './utils/const'

import { DuckDBService } from '@/services/duckdb'

import { getFilterValues } from '../utils/helpers'
import { getCurrentDate } from '@/helpers/dates'

import useAnalyticsStore from '../index'

export const TABLE_NAME = 'analytics_commitments'

const TABLE_SCHEMA: Record<string, DuckDBSchemaType> = {
  [AnalyticsField.DATE]: 'DATE',
  [AnalyticsField.ACCOUNT_ID]: 'VARCHAR',
  [AnalyticsField.ASSET_ID]: 'VARCHAR',
  [AnalyticsField.INCEPTION_DATE]: 'DATE',
  [AnalyticsField.ORIGINAL_COMMITMENT]: 'DOUBLE',
  [AnalyticsField.REMAINING_COMMITMENT]: 'DOUBLE',
  [AnalyticsField.INVESTED_CAPITAL]: 'DOUBLE',
  [AnalyticsField.RECALLABLE_CAPITAL]: 'DOUBLE',
  [AnalyticsField.RETURNED_CAPITAL]: 'DOUBLE',
  [AnalyticsField.INCOME]: 'DOUBLE',
  [AnalyticsField.TOTAL_DISTRIBUTION]: 'DOUBLE',
  [AnalyticsField.GAIN_LOSS]: 'DOUBLE',
  [AnalyticsField.CURRENT_BALANCE]: 'DOUBLE',
}

export default defineStore('analytics-commitments', () => {
  // INIT
  const duckdbInstance = DuckDBService.instance()

  const loading = ref(false)
  const deprecated = ref<boolean>()
  const trigger = ref(0)

  const datesRange = ref<string[]>([])

  // GETTERS
  const isInit = computed(() => deprecated.value !== undefined)

  const getDatesRange = computed(() => datesRange.value)

  // SETTERS
  const setList = async (data: {
    commitments_view: AnalyticsCommitment[]
  }) => {
    if (deprecated.value === false) return
    if (!data.commitments_view) return
    deprecated.value = false
    await duckdbInstance.createTable(TABLE_NAME, TABLE_SCHEMA)
    await duckdbInstance.pushData(TABLE_NAME, data.commitments_view, TABLE_SCHEMA)
    trigger.value++
  }

  // ACTIONS
  const clear = () => {
    loading.value = false
    deprecated.value = undefined
    trigger.value = 0
  }

  return {
    isLoading: loading,
    isDeprecated: deprecated,

    isInit,
    trigger,

    getDatesRange,

    setList,

    clear,
  }
})

export const useAnalyticsCommitmentsFilters = () => {

  const analyticsStore = useAnalyticsStore()

  const analyticsDate = computed(() => analyticsStore.getDate || getCurrentDate({}))

  const getValues = getFilterValues(TABLE_NAME, analyticsDate)

  const filters = computed(() => {
    const result: FilterField[] = [
      {
        name: AnalyticsField.ACCOUNT_NAME,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ACCOUNT],
        getter: () => getValues(AnalyticsField.ACCOUNT_ID)
      },
      {
        name: AnalyticsField.ASSET_NAME,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.ASSET],
        getter: () => getValues(AnalyticsField.ASSET_ID)
      },
      {
        name: AnalyticsField.INCEPTION_DATE,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.INCEPTION_DATE],
        isDate: true,
      },
      ...COMMITMENTS_POSIBLE_VALUES.map(name => ({
        name,
        caption: ANALYTICS_CAPTIONS[name as keyof typeof ANALYTICS_CAPTIONS],
        isAmount: true,
      })),
      {
        name: AnalyticsField.IRR,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.IRR],
        isAmount: true,
      },
      {
        name: AnalyticsField.MOIC,
        caption: ANALYTICS_CAPTIONS[AnalyticsField.MOIC],
        isAmount: true,
      },
    ]
    return orderBy(result, 'caption')
  })

  return {
    filters,
  }
}
